import React, { useCallback, useMemo, useState } from "react";
import { WizardShell } from "../components/healthcheck/WizardShell";
import { StepIntro } from "../components/healthcheck/StepIntro";
import { StepAgeGate } from "../components/healthcheck/StepAgeGate";
import { StepCatBasics } from "../components/healthcheck/StepCatBasics";
import { StepPhase1 } from "../components/healthcheck/StepPhase1";
import { StepPhase2 } from "../components/healthcheck/StepPhase2";
import { StepLoading } from "../components/healthcheck/StepLoading";
import { StepPhotoCheck } from "../components/healthcheck/StepPhotoCheck";
import { StepEmailGate } from "../components/healthcheck/StepEmailGate";
import { StepReport } from "../components/healthcheck/StepReport";
import { PHASE1_QUESTIONS, REPORT_COPY } from "../lib/content";
import {
    analyzePhoto,
    apiErrorMessage,
    createLead,
    generatePhase2,
    generateReport,
} from "../lib/api";

/**
 * Steps enum — one per screen. Deliberately explicit for testing clarity.
 */
const STEP = {
    INTRO: "intro",
    AGE: "age",
    BASICS: "basics",
    PHASE1: "phase1",
    PHASE2_LOAD: "phase2-load",
    PHASE2: "phase2",
    PHOTO: "photo",
    PHOTO_LOAD: "photo-load",
    EMAIL: "email",
    REPORT_LOAD: "report-load",
    REPORT: "report",
};

const emptyCat = { name: "", breed: "", age: "", weight_kg: "" };

export default function HealthCheck() {
    const [step, setStep] = useState(STEP.INTRO);
    const [phase1Index, setPhase1Index] = useState(0);
    const [phase2Index, setPhase2Index] = useState(0);
    const [ageAgreed, setAgeAgreed] = useState(false);
    const [cat, setCat] = useState(emptyCat);
    const [phase1Answers, setPhase1Answers] = useState({});
    const [phase2Questions, setPhase2Questions] = useState(null);
    const [phase2Answers, setPhase2Answers] = useState({});
    const [photoResult, setPhotoResult] = useState(null);
    const [photoError, setPhotoError] = useState(null);
    const [photoSubmitting, setPhotoSubmitting] = useState(false);
    const [email, setEmail] = useState("");
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [leadSubmitting, setLeadSubmitting] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [report, setReport] = useState(null);
    const [loadError, setLoadError] = useState(null);

    const catPayload = useMemo(
        () => ({
            name: (cat.name || "").trim(),
            breed: (cat.breed || "").trim() || null,
            age: Number(cat.age) || 0,
            weight_kg: Number(cat.weight_kg) || 0,
        }),
        [cat],
    );

    /* ---------- Step count for progress bar (7 baseline steps + N phase-2) */
    const total = useMemo(() => {
        // basics + 7 phase1 + phase2 (variable) + photo + email + report
        const p2 = phase2Questions?.questions?.length || 10;
        return 1 + PHASE1_QUESTIONS.length + p2 + 1 + 1 + 1; // = 21 default
    }, [phase2Questions]);

    const currentStepIndex = useMemo(() => {
        if (step === STEP.INTRO || step === STEP.AGE) return 0;
        if (step === STEP.BASICS) return 1;
        if (step === STEP.PHASE1) return 1 + phase1Index + 1;
        if (step === STEP.PHASE2_LOAD) return 1 + PHASE1_QUESTIONS.length + 1;
        if (step === STEP.PHASE2)
            return 1 + PHASE1_QUESTIONS.length + phase2Index + 1;
        const p2n = phase2Questions?.questions?.length || 10;
        if (step === STEP.PHOTO || step === STEP.PHOTO_LOAD)
            return 1 + PHASE1_QUESTIONS.length + p2n + 1;
        if (step === STEP.EMAIL)
            return 1 + PHASE1_QUESTIONS.length + p2n + 2;
        if (step === STEP.REPORT_LOAD || step === STEP.REPORT) return total;
        return 0;
    }, [step, phase1Index, phase2Index, phase2Questions, total]);

    /* ---------- Actions ----------------------------------------------- */
    const answerPhase1 = useCallback((id, val) => {
        setPhase1Answers((prev) => ({ ...prev, [id]: val }));
    }, []);

    const answerPhase2 = useCallback((id, val) => {
        setPhase2Answers((prev) => ({ ...prev, [id]: val }));
    }, []);

    const advanceFromPhase1 = useCallback(async () => {
        if (phase1Index < PHASE1_QUESTIONS.length - 1) {
            setPhase1Index((i) => i + 1);
            return;
        }
        // finished phase 1 → generate phase 2
        setStep(STEP.PHASE2_LOAD);
        setLoadError(null);
        try {
            const res = await generatePhase2({
                cat: catPayload,
                phase1: { answers: phase1Answers },
            });
            setPhase2Questions(res);
            setPhase2Index(0);
            setStep(STEP.PHASE2);
        } catch (err) {
            setLoadError(apiErrorMessage(err, "Phase 2 could not be generated."));
        }
    }, [phase1Index, phase1Answers, catPayload]);

    const advanceFromPhase2 = useCallback(() => {
        const qs = phase2Questions?.questions || [];
        if (phase2Index < qs.length - 1) {
            setPhase2Index((i) => i + 1);
            return;
        }
        setStep(STEP.PHOTO);
    }, [phase2Index, phase2Questions]);

    const submitPhoto = useCallback(
        async (file) => {
            setPhotoSubmitting(true);
            setPhotoError(null);
            setStep(STEP.PHOTO_LOAD);
            try {
                const res = await analyzePhoto({ cat: catPayload, file });
                setPhotoResult(res);
                setStep(STEP.EMAIL);
            } catch (err) {
                setPhotoError(apiErrorMessage(err, "Photo analysis failed."));
                setStep(STEP.PHOTO);
            } finally {
                setPhotoSubmitting(false);
            }
        },
        [catPayload],
    );

    const skipPhoto = useCallback(() => {
        setPhotoResult(null);
        setPhotoError(null);
        setStep(STEP.EMAIL);
    }, []);

    const submitEmail = useCallback(async () => {
        setEmailError(null);
        setLeadSubmitting(true);
        setStep(STEP.REPORT_LOAD);
        setLoadError(null);
        try {
            const rep = await generateReport({
                cat: catPayload,
                phase1: { answers: phase1Answers },
                phase2_questions: phase2Questions,
                phase2_answers: phase2Answers,
                photo_check: photoResult,
            });
            setReport(rep);
            // persist lead + report; email delivery is stubbed server-side.
            try {
                await createLead({
                    email: email.trim(),
                    marketing_opt_in: marketingOptIn,
                    cat: catPayload,
                    phase1: { answers: phase1Answers },
                    phase2_questions: phase2Questions,
                    phase2_answers: phase2Answers,
                    photo_check: photoResult,
                    report: rep,
                });
            } catch (leadErr) {
                // Non-blocking — the user still gets to see their report.
                console.warn("Lead persistence failed:", leadErr);
            }
            setStep(STEP.REPORT);
        } catch (err) {
            setLoadError(apiErrorMessage(err, "Report generation failed."));
        } finally {
            setLeadSubmitting(false);
        }
    }, [
        catPayload,
        email,
        marketingOptIn,
        phase1Answers,
        phase2Answers,
        phase2Questions,
        photoResult,
    ]);

    const goBack = useCallback(() => {
        setLoadError(null);
        switch (step) {
            case STEP.AGE:
                return setStep(STEP.INTRO);
            case STEP.BASICS:
                return setStep(STEP.AGE);
            case STEP.PHASE1:
                if (phase1Index === 0) return setStep(STEP.BASICS);
                return setPhase1Index((i) => i - 1);
            case STEP.PHASE2_LOAD:
                return setStep(STEP.PHASE1);
            case STEP.PHASE2:
                if (phase2Index === 0) {
                    setPhase1Index(PHASE1_QUESTIONS.length - 1);
                    return setStep(STEP.PHASE1);
                }
                return setPhase2Index((i) => i - 1);
            case STEP.PHOTO:
                setPhase2Index((phase2Questions?.questions?.length || 1) - 1);
                return setStep(STEP.PHASE2);
            case STEP.EMAIL:
                return setStep(STEP.PHOTO);
            case STEP.REPORT_LOAD:
                return setStep(STEP.EMAIL);
            default:
                return;
        }
    }, [step, phase1Index, phase2Index, phase2Questions]);

    /* ---------- Render ------------------------------------------------ */
    const showBack =
        step !== STEP.INTRO &&
        step !== STEP.REPORT &&
        step !== STEP.PHOTO_LOAD &&
        step !== STEP.REPORT_LOAD;

    const shellProps = {
        step: currentStepIndex,
        total,
        onBack: showBack ? goBack : undefined,
    };

    if (step === STEP.INTRO) {
        return (
            <WizardShell {...shellProps} step={0} total={0}>
                <StepIntro onNext={() => setStep(STEP.AGE)} />
            </WizardShell>
        );
    }

    if (step === STEP.AGE) {
        return (
            <WizardShell {...shellProps} step={0} total={0}>
                <StepAgeGate
                    agreed={ageAgreed}
                    onAgreedChange={setAgeAgreed}
                    onNext={() => setStep(STEP.BASICS)}
                />
            </WizardShell>
        );
    }

    if (step === STEP.BASICS) {
        return (
            <WizardShell {...shellProps}>
                <StepCatBasics
                    value={cat}
                    onChange={setCat}
                    onNext={() => {
                        setPhase1Index(0);
                        setStep(STEP.PHASE1);
                    }}
                />
            </WizardShell>
        );
    }

    if (step === STEP.PHASE1) {
        return (
            <WizardShell {...shellProps}>
                <StepPhase1
                    index={phase1Index}
                    answers={phase1Answers}
                    onAnswer={answerPhase1}
                    onNext={advanceFromPhase1}
                    catName={catPayload.name}
                />
            </WizardShell>
        );
    }

    if (step === STEP.PHASE2_LOAD) {
        return (
            <WizardShell {...shellProps}>
                <StepLoading
                    variant="phase2"
                    error={loadError}
                    onRetry={advanceFromPhase1}
                    onBack={() => {
                        setLoadError(null);
                        setStep(STEP.PHASE1);
                    }}
                />
            </WizardShell>
        );
    }

    if (step === STEP.PHASE2) {
        return (
            <WizardShell {...shellProps}>
                <StepPhase2
                    index={phase2Index}
                    questions={phase2Questions?.questions || []}
                    answers={phase2Answers}
                    onAnswer={answerPhase2}
                    onNext={advanceFromPhase2}
                    catName={catPayload.name}
                />
            </WizardShell>
        );
    }

    if (step === STEP.PHOTO) {
        return (
            <WizardShell {...shellProps}>
                <StepPhotoCheck
                    onAnalyze={submitPhoto}
                    onSkip={skipPhoto}
                    submitting={photoSubmitting}
                    error={photoError}
                    catName={catPayload.name}
                />
            </WizardShell>
        );
    }

    if (step === STEP.PHOTO_LOAD) {
        return (
            <WizardShell {...shellProps}>
                <StepLoading
                    variant="phase2"
                    error={photoError}
                    onRetry={() => setStep(STEP.PHOTO)}
                    onBack={() => setStep(STEP.PHOTO)}
                />
            </WizardShell>
        );
    }

    if (step === STEP.EMAIL) {
        return (
            <WizardShell {...shellProps}>
                <StepEmailGate
                    email={email}
                    marketingOptIn={marketingOptIn}
                    onEmailChange={setEmail}
                    onOptInChange={setMarketingOptIn}
                    onNext={submitEmail}
                    submitting={leadSubmitting}
                    error={emailError}
                />
            </WizardShell>
        );
    }

    if (step === STEP.REPORT_LOAD) {
        return (
            <WizardShell {...shellProps}>
                <StepLoading
                    variant="report"
                    error={loadError}
                    onRetry={submitEmail}
                    onBack={() => {
                        setLoadError(null);
                        setStep(STEP.EMAIL);
                    }}
                />
            </WizardShell>
        );
    }

    // REPORT
    return (
        <WizardShell step={total} total={total}>
            <StepReport
                report={report}
                cat={catPayload}
                onDone={() => {
                    window.location.href = REPORT_COPY.ctaContinueUrl;
                }}
            />
        </WizardShell>
    );
}
